import parsePhoneNumberFromString from 'libphonenumber-js/max';
import type { IntlShape } from 'react-intl/src/types';
import type { InferType } from 'yup';
import { bool, object, string } from 'yup';

import type { Country } from '@/types/regionTypes';

import type { CountryCode, UserFragment } from '@/graphql/saleor/api';
import { EMAIL_REGX, MAX_CHAR, MAX_CHAR_PHONE } from '@/lib/forms/const';
import { getNameSchema, getTitleSchema } from '@/lib/forms/schemas';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { parsePhoneNumber } from '@/lib/phone';
import {
  getPhonePrefixSelectOptByCode,
  handlePhonePrefix,
} from '@/lib/phonePrefixes/helpers';
import { formMessages } from '@/messages/forms';
import type { PhonePrefix } from '@/views/DeliveryView/helpers';

export type CampaignSubscriptionSchema = InferType<
  ReturnType<typeof campaignSubscriptionSchema>
> & { reCaptchaResponse: string };

export const campaignSubscriptionSchema = (
  user: Partial<UserFragment> | null,
  country: Country,
  formatMessage: IntlShape['formatMessage'],
) => {
  const requiredMsg = formatMessage(formMessages.requiredField);
  const maxCharMsg = formatMessage(formMessages.maxChars, {
    max: MAX_CHAR,
  });

  const title = getMetaValue(
    user?.metadata || [],
    ACCOUNT_METADATA.personalTitle,
  );
  let newsletterConsent = false;
  if (user?.metadata) {
    const __newsletterConsent =
      getMetaValue(user.metadata, ACCOUNT_METADATA.newsletterConsent) ??
      'false';
    newsletterConsent = ['true', true]?.includes(__newsletterConsent);
  }
  const userPhone =
    getMetaValue(user?.metadata || [], ACCOUNT_METADATA.phone) ?? '';
  const userPhonePrefix = handlePhonePrefix(
    getMetaValue(user?.metadata || [], ACCOUNT_METADATA.phonePrefix) ?? '',
  );
  const countryCode = ((userPhone &&
    userPhonePrefix &&
    parsePhoneNumber(userPhonePrefix + userPhone)?.country) ||
    country.code) as CountryCode;
  return object({
    title: getTitleSchema(formatMessage, countryCode, title),
    firstName: string()
      .required(requiredMsg)
      .max(MAX_CHAR, maxCharMsg)
      .default(user?.firstName ?? ''),
    lastName: string()
      .required(requiredMsg)
      .max(MAX_CHAR, maxCharMsg)
      .default(user?.lastName ?? ''),
    ...(country.code === 'JP' && {
      firstNameKanji: getNameSchema(formatMessage, 'firstKanji'),
      lastNameKanji: getNameSchema(formatMessage, 'lastKanji'),
    }),
    email: string()
      .required(requiredMsg)
      .matches(EMAIL_REGX, formatMessage(formMessages.invalidEmailAddress))
      .default(user?.email ?? ''),
    phonePrefix: object({
      value: string().required(requiredMsg),
      label: string().required(requiredMsg),
    }).default(getPhonePrefixSelectOptByCode(countryCode)),
    phone: string()
      .default(userPhone)
      .required(requiredMsg)
      .max(
        MAX_CHAR_PHONE,
        formatMessage(formMessages.maxChars, {
          max: MAX_CHAR_PHONE,
        }),
      )
      .when('phonePrefix', {
        is: (phonePrefix: PhonePrefix) => !!phonePrefix,
        then: (schema) =>
          schema.test(
            'is-valid-phone',
            formatMessage(formMessages.invalidPhone),
            function (value) {
              const { phonePrefix } = this.parent;
              const fullPhoneNumber = `${phonePrefix.value}${value}`;
              const phoneNumber = parsePhoneNumberFromString(fullPhoneNumber);
              return phoneNumber ? phoneNumber.isValid() : false;
            },
          ),
      }),
    newsletterConsent: bool()
      .default(newsletterConsent)
      .oneOf([true], requiredMsg),
    captcha: bool().default(false).oneOf([true], requiredMsg),
  });
};
